import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";

/**
 * Layer 2 anomaly: a FORMULA cell whose R1C1 pattern id differs from
 * the band's dominant pattern. Detected at band granularity (row-band
 * OR column-band within a region) — strictly more local than the
 * existing `inconsistentFormulas` rule, which only looks at full
 * columns.
 *
 * Both rules can coexist and surface different findings:
 *   - `inconsistentFormulas` (column-only) flags cells where a
 *     column's majority formula shape differs.
 *   - This rule flags cells where a row-band OR column-band within a
 *     region has a dominant pattern that the cell breaks.
 *
 * Band-scoped scope finds the same anomalies the column rule does
 * plus row-band ones the column rule can't see (e.g. a forecast-row
 * formula one column over from its neighbors). Cells flagged by both
 * rules will produce two findings in the Review dialog — distinct
 * messages, both informative.
 */
export const inconsistentFormulaInBand: AuditRule = {
  id: "system.audit.inconsistent-formula-in-band",
  name: "Inconsistent formula in a band",
  description:
    "A formula cell whose R1C1 pattern differs from the dominant pattern of " +
    "its row-band or column-band within a region. Catches one-off overrides " +
    "and copy-paste errors that the column-only inconsistentFormulas rule " +
    "misses on row-oriented bands.",
  enabled: true,
  source: "system",
  severity: "warning",
  needsPatternAnalysis: true,
  check(sheet) {
    if (!sheet.patternAnalysis) return [];
    const findings: AuditFindingPartial[] = [];
    const { startRow, startCol } = sheet.bounds;

    for (const a of sheet.patternAnalysis.anomalies) {
      if (a.type !== "inconsistent_formula") continue;
      findings.push({
        sheetName: sheet.name,
        address: addressFromRC(startRow + a.row, startCol + a.col),
        message: `formula differs from its band's dominant pattern (severity ${a.severityScore.toFixed(2)})`,
      });
    }
    return findings;
  },
};
